import { useEffect, useState } from "react";

import DatePicker from "common/components/DatePicker";
import { Button } from "common/components/ui/Button";
import { DialogTitle } from "common/components/ui/Dialog";
import { DrawerTitle } from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import { getMonthName } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { TIMEZONES } from "../constants";
import useCampaignMutations from "../datahooks/useCampaignMutations";
import TimezoneSelect from "./TimezoneSelect";

interface CampaignSchedulingProps {
  campaignId: string;
  onCancel: () => void;
  onSuccess: () => void;
}

export default function CampaignScheduling({
  campaignId,
  onCancel,
  onSuccess,
}: CampaignSchedulingProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { scheduleCampaign, isSchedulingCampaign } = useCampaignMutations();
  const [startDate, setStartDate] = useState(new Date());
  const [startTime, setStartTime] = useState<string>();
  const [dateTimeError, setDateTimeError] = useState("");
  const [selectedTimezone, setSelectedTimezone] = useState(
    TIMEZONES.find(({ value }) => value === "UTC"),
  );

  const startDateTime = new Date(startDate);

  if (startTime) {
    const [hours, minutes] = startTime.split(":");
    startDateTime.setUTCHours(Number(hours));
    startDateTime.setUTCMinutes(Number(minutes));
  }

  const isDateTimeValid = startDateTime > new Date();

  useEffect(() => {
    if (dateTimeError && isDateTimeValid) {
      setDateTimeError("");
    }
  }, [startDateTime]);

  function onSchedule() {
    // make sure the date is in the future
    if (isDateTimeValid) {
      const timezoneOffsetDate = new Date(
        startDateTime.getTime() - selectedTimezone.offset * 60 * 60 * 1000,
      );
      scheduleCampaign({
        campaignId,
        startDate: timezoneOffsetDate.toISOString(),
      }).then(onSuccess);
    } else {
      setDateTimeError("Cannot schedule for past date");
    }
  }

  const Title = isDesktop ? DialogTitle : DrawerTitle;

  return (
    <>
      <Title className="mb-8">Pick date & time</Title>

      <div className="flex flex-col gap-5 sm:flex-row">
        <div className="shrink-0 rounded-5xl border border-black-200 max-lg:mx-auto">
          <DatePicker
            mode="single"
            disabled={{ before: new Date() }}
            selected={startDate}
            required
            onSelect={(date) => {
              setStartDate(date);
            }}
          />
        </div>
        <div className="-mx-1 flex flex-1 flex-col gap-3 overflow-hidden px-1">
          <FormField
            label="Chosen date"
            htmlFor="date-input"
            className="max-lg:flex-1"
          >
            <Input
              value={`${startDate.getDate()} ${getMonthName(startDate.getMonth(), true)} ${startDate.getFullYear()}`}
              variant="md"
              id="date-input"
              disabled
            />
          </FormField>
          <FormField label="Choose time" htmlFor="time-input">
            <input
              id="time-input"
              type="time"
              value={startTime}
              onChange={({ target }) => setStartTime(target.value)}
              className="h-10 rounded-2xl bg-whiteGray px-3 text-button-sm text-black-700 placeholder:text-black-400 focus-visible:outline-purple-200"
            />
          </FormField>
          <FormField label="Choose timezone" htmlFor="timezone-select">
            <TimezoneSelect
              selectedTimezone={selectedTimezone}
              onSelect={setSelectedTimezone}
              isDisabled={false}
              isScheduling
              className="w-full"
            />
          </FormField>
        </div>
      </div>

      {dateTimeError && (
        <span className="mt-2 text-xs text-red-500">{dateTimeError}</span>
      )}

      <div className="mt-8 flex justify-between gap-x-2.5">
        <Button
          variant="secondary-black"
          className="lg:flex-1"
          onClick={onCancel}
          disabled={isSchedulingCampaign}
        >
          Cancel
        </Button>

        <Button
          className="lg:flex-1"
          onClick={onSchedule}
          isLoading={isSchedulingCampaign}
          disabled={!startTime}
        >
          Schedule campaign
        </Button>
      </div>
    </>
  );
}
